import {
	type InfiniteData,
	type QueryClient,
	useInfiniteQuery,
	useMutation,
	useQuery,
	useQueryClient,
} from "@tanstack/react-query";
import { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";

import { api } from "@/api";
import type {
	NotificationCategory,
	NotificationItem,
	NotificationListQuery,
	NotificationLocale,
	NotificationPage,
	NotificationPreferences,
	NotificationPreferencesUpdate,
	NotificationReadBySourceRequest,
	NotificationUnreadCount,
} from "@/api/types";
import { useProfile } from "@/store/profile";

const localeOf = (locale: string): NotificationLocale =>
	locale.startsWith("en") ? "en" : locale.startsWith("hy") ? "hy" : "ru";

export const notificationCountPollInterval = (
	authenticated: boolean,
	visibility: DocumentVisibilityState,
): 30000 | false => (authenticated && visibility === "visible" ? 30_000 : false);

export const notificationKeys = {
	root: (principalId: string) => ["notifications", principalId] as const,
	count: (principalId: string) => [...notificationKeys.root(principalId), "count"] as const,
	listRoot: (principalId: string) => [...notificationKeys.root(principalId), "list"] as const,
	list: (
		principalId: string,
		locale: NotificationLocale,
		filter: "all" | "unread",
		limit: number,
		category: NotificationCategory | "all" = "all",
	) => [...notificationKeys.listRoot(principalId), locale, filter, limit, category] as const,
	detail: (principalId: string, locale: NotificationLocale, id: string) =>
		[...notificationKeys.root(principalId), "detail", locale, id] as const,
	preferences: (principalId: string) => [...notificationKeys.root(principalId), "preferences"] as const,
};

export const markItemRead = (item: NotificationItem, id: string, at: string): NotificationItem =>
	item.id === id && !item.readAt ? { ...item, readAt: at } : item;

export const markPageItemRead = (
	data: InfiniteData<NotificationPage> | undefined,
	id: string,
	at: string,
): InfiniteData<NotificationPage> | undefined =>
	data
		? {
				...data,
				pages: data.pages.map((page) => ({
					...page,
					items: page.items.map((item) => markItemRead(item, id, at)),
				})),
			}
		: data;

export const markAllPageItemsRead = (
	data: InfiniteData<NotificationPage> | undefined,
	at: string,
): InfiniteData<NotificationPage> | undefined =>
	data
		? {
				...data,
				pages: data.pages.map((page) => ({
					...page,
					items: page.items.map((item) => (item.readAt ? item : { ...item, readAt: at })),
				})),
			}
		: data;

const patchLists = (
	queryClient: QueryClient,
	principalId: string,
	patch: (data: InfiniteData<NotificationPage> | undefined) => InfiniteData<NotificationPage> | undefined,
) => queryClient.setQueriesData<InfiniteData<NotificationPage>>({ queryKey: notificationKeys.listRoot(principalId) }, patch);

export const decrementNotificationCount = (
	count: NotificationUnreadCount | undefined,
): NotificationUnreadCount | undefined => (count ? { count: Math.max(0, count.count - 1) } : count);

const itemWasUnread = (queryClient: QueryClient, principalId: string, id: string) => {
	for (const [, value] of queryClient.getQueriesData<InfiniteData<NotificationPage>>({ queryKey: notificationKeys.listRoot(principalId) })) {
		if (value?.pages.some((page) => page.items.some((item) => item.id === id && !item.readAt))) return true;
	}
	return false;
};

const invalidatePrincipal = async (queryClient: QueryClient, principalId: string) => {
	await queryClient.invalidateQueries({ queryKey: notificationKeys.root(principalId) });
};

export function useNotificationSession() {
	const principalId = useProfile((state) => state.user?.id ?? null);
	const queryClient = useQueryClient();
	const previousPrincipal = useRef<string | null>(null);

	useEffect(() => {
		const previous = previousPrincipal.current;
		if (previous && previous !== principalId) {
			void queryClient.cancelQueries({ queryKey: notificationKeys.root(previous) }).then(() => {
				queryClient.removeQueries({ queryKey: notificationKeys.root(previous) });
			});
		}
		previousPrincipal.current = principalId;
	}, [principalId, queryClient]);
}

export function useNotificationCount() {
	const token = useProfile((state) => state.token);
	const user = useProfile((state) => state.user);
	const hydrating = useProfile((state) => state.isHydrating);
	const principalId = user?.id ?? "guest";
	const enabled = Boolean(token && user?.id && !hydrating);
	return useQuery({
		queryKey: notificationKeys.count(principalId),
		queryFn: () => api.notifications.unreadCount(),
		enabled,
		staleTime: 30_000,
		refetchInterval: () =>
			notificationCountPollInterval(
				enabled,
				typeof document === "undefined" ? "hidden" : document.visibilityState,
			),
		refetchIntervalInBackground: false,
		refetchOnWindowFocus: true,
		refetchOnReconnect: true,
	});
}

export function useNotificationList({
	filter = "all",
	limit = 20,
	category = "all",
	enabled: enabledBySurface = true,
}: {
	filter?: "all" | "unread";
	limit?: number;
	// fd-notif-17: фильтр ленты по категории. "all" — без фильтра.
	category?: NotificationCategory | "all";
	enabled?: boolean;
} = {}) {
	const { i18n } = useTranslation();
	const locale = localeOf(i18n.resolvedLanguage ?? i18n.language);
	const token = useProfile((state) => state.token);
	const user = useProfile((state) => state.user);
	const hydrating = useProfile((state) => state.isHydrating);
	const principalId = user?.id ?? "guest";
	const enabled = enabledBySurface && Boolean(token && user?.id && !hydrating);
	return useInfiniteQuery({
		queryKey: notificationKeys.list(principalId, locale, filter, limit, category),
		queryFn: ({ pageParam }) =>
			api.notifications.list({
				cursor: pageParam,
				limit,
				filter,
				locale,
				...(category === "all" ? {} : { category }),
			} satisfies NotificationListQuery),
		initialPageParam: undefined as string | undefined,
		getNextPageParam: (lastPage) => (lastPage.hasMore ? lastPage.nextCursor ?? undefined : undefined),
		enabled,
		staleTime: 15_000,
		refetchOnWindowFocus: true,
		refetchOnReconnect: true,
	});
}

export function useNotificationDetail(id: string | null) {
	const { i18n } = useTranslation();
	const locale = localeOf(i18n.resolvedLanguage ?? i18n.language);
	const token = useProfile((state) => state.token);
	const user = useProfile((state) => state.user);
	const principalId = user?.id ?? "guest";
	return useQuery({
		queryKey: notificationKeys.detail(principalId, locale, id ?? "missing"),
		queryFn: () => api.notifications.detail(id ?? "", locale),
		enabled: Boolean(id && token && user?.id),
		refetchOnWindowFocus: true,
	});
}

export function useMarkNotificationRead() {
	const principalId = useProfile((state) => state.user?.id ?? "guest");
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: (id: string) => api.notifications.markRead(id),
		onMutate: async (id) => {
			await queryClient.cancelQueries({ queryKey: notificationKeys.root(principalId) });
			const snapshot = queryClient.getQueriesData({ queryKey: notificationKeys.root(principalId) });
			const wasUnread = itemWasUnread(queryClient, principalId, id);
			const at = new Date().toISOString();
			patchLists(queryClient, principalId, (data) => markPageItemRead(data, id, at));
			queryClient.setQueriesData<NotificationItem>(
				{ queryKey: notificationKeys.root(principalId), predicate: (query) => query.queryKey.includes("detail") },
				(item) => (item ? markItemRead(item, id, at) : item),
			);
			if (wasUnread) {
				queryClient.setQueryData<NotificationUnreadCount>(
					notificationKeys.count(principalId),
					decrementNotificationCount,
				);
			}
			return { snapshot };
		},
		onError: (_error, _id, context) => {
			for (const [key, value] of context?.snapshot ?? []) queryClient.setQueryData(key, value);
		},
		onSettled: () => invalidatePrincipal(queryClient, principalId),
	});
}

export function useMarkAllNotificationsRead() {
	const principalId = useProfile((state) => state.user?.id ?? "guest");
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: () => api.notifications.markAllRead(),
		onMutate: async () => {
			await queryClient.cancelQueries({ queryKey: notificationKeys.root(principalId) });
			const snapshot = queryClient.getQueriesData({ queryKey: notificationKeys.root(principalId) });
			const at = new Date().toISOString();
			patchLists(queryClient, principalId, (data) => markAllPageItemsRead(data, at));
			queryClient.setQueriesData<NotificationItem>(
				{ queryKey: notificationKeys.root(principalId), predicate: (query) => query.queryKey.includes("detail") },
				(item) => (item?.readAt ? item : item ? { ...item, readAt: at } : item),
			);
			queryClient.setQueryData<NotificationUnreadCount>(notificationKeys.count(principalId), { count: 0 });
			return { snapshot };
		},
		onError: (_error, _variables, context) => {
			for (const [key, value] of context?.snapshot ?? []) queryClient.setQueryData(key, value);
		},
		onSettled: () => invalidatePrincipal(queryClient, principalId),
	});
}

export function useMarkNotificationReadBySource() {
	const principalId = useProfile((state) => state.user?.id ?? "guest");
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: (request: NotificationReadBySourceRequest) => api.notifications.markReadBySource(request),
		onSettled: () => invalidatePrincipal(queryClient, principalId),
	});
}

// fd-notif-02/14/15/20: настройки уведомлений покупателя.
export function useNotificationPreferences() {
	const token = useProfile((state) => state.token);
	const user = useProfile((state) => state.user);
	const hydrating = useProfile((state) => state.isHydrating);
	const principalId = user?.id ?? "guest";
	return useQuery({
		queryKey: notificationKeys.preferences(principalId),
		queryFn: () => api.notifications.getPreferences(),
		enabled: Boolean(token && user?.id && !hydrating),
		staleTime: 30_000,
	});
}

export function useUpdateNotificationPreferences() {
	const principalId = useProfile((state) => state.user?.id ?? "guest");
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: (patch: NotificationPreferencesUpdate) => api.notifications.updatePreferences(patch),
		onSuccess: (preferences: NotificationPreferences) => {
			queryClient.setQueryData(notificationKeys.preferences(principalId), preferences);
		},
	});
}
